import React, { useEffect, useState } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import {
	Grid,
	Col,
	Text,
	CloudinaryImage,
	Icon,
	Slider,
	Button,
	LineGraphic,
	Separator,
} from "@website-builder/ui/shared/elements";
import {
	Wrapper,
	ImageTestimonialWrapper,
	QuoteCardContent,
	QuoteTestimonialWrapper,
	ThreeUpWrapper,
	Image,
} from "./styles";
import { isBusinessVerticalB2U } from "@website-builder/utilities/utils/helpers.js";
import { TABLET_BP } from "@website-builder/utilities/constants/style-constants/screen-sizes.js";
import { handleCTAClick } from "@website-builder/utilities/utils/DesignUtils.js";
const isB2U = isBusinessVerticalB2U();

const Testimonial = ({
	cards,
	darkBg,
	description,
	heading,
	mobileLayout,
	layout,
	showNavigationInMobile,
	cta,
	backgroundColor,
	quoteColor,
	pageSection,
}) => {
	// checks for mobile width
	const satisfiesMobileWidth = useMediaQuery(`(max-width: ${TABLET_BP}px)`);
	const [isMobile, setIsMobile] = useState(undefined);

	useEffect(() => {
		setIsMobile(satisfiesMobileWidth);
	}, [satisfiesMobileWidth]);

	const sliderSettings = {
		dots: true,
		infinite: true,
		slidesToShow: 1,
		slidesToScroll: 1,
		speed: 500,
		arrows: true,
	};
	const darkTextStyles = darkBg
		? { color: "var(--primary-neutral-white)" }
		: null;

	const getImageCard = (card) => {
		return (
			<div className="slider-child" key={card._uid}>
				<div className="content-flex">
					<div className="cloudinary-image-container">
						<CloudinaryImage
							url={card.imageURL}
							quality="auto"
							fetchFormat="auto"
							lazyload
							sizes={{
								default: {
									height: 500,
								},
							}}
							alt={card.imageAlt}
						/>
					</div>
					<div className="text-container">
						<div className="inner-text-container">
							{card.label ? (
								<Text variant="label_M" className="text-label">
									{card.label}
								</Text>
							) : null}
							<Icon fill={isB2U ? quoteColor : undefined} iconName="quote" />
							<Text variant="paragraph_M" className="text-quote">
								{card.quote}
							</Text>
							<Separator />
							<div className="bottom-section">
								<div className="student-info">
									{card.studentName ? (
										<Text variant="paragraph_M_bold">{card.studentName}</Text>
									) : null}
									{card.studentWork ? (
										<Text variant="paragraph_M">{card.studentWork}</Text>
									) : null}
									{card.studentTransition ? (
										<Text variant="paragraph_M" className="gray-text" tag="div">
											{card.studentTransition}
										</Text>
									) : null}
								</div>
								<CloudinaryImage
									url={card.logoURL}
									quality="auto"
									fetchFormat="auto"
									lazyload
									sizes={{
										default: {
											height: 32,
										},
									}}
									alt={card.logoAlt}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const getQuoteCardContent = (card, quoteVariant = "headline_6") => {
		return (
			<QuoteCardContent>
				<div>
					{card.label ? (
						<Text variant="label_M" className="text-label">
							{card.label}
						</Text>
					) : null}
					<Icon fill={isB2U ? quoteColor : undefined} iconName="quote" />
					<Text variant={quoteVariant} className="text-quote">
						{card.quote}
					</Text>
				</div>
				<div>
					<Separator />
					<div className="student-flex">
						<div className="student-info">
							{card.studentName ? (
								<Text variant="paragraph_M_bold">{card.studentName}</Text>
							) : null}
							{card.studentWork ? (
								<Text variant="paragraph_M">{card.studentWork}</Text>
							) : null}
							{card.studentTransition ? (
								<Text variant="paragraph_M" className="gray-text" tag="div">
									{card.studentTransition}
								</Text>
							) : null}
							<CloudinaryImage
								url={card.logoURL}
								quality="auto"
								fetchFormat="auto"
								lazyload
								sizes={{
									default: {
										height: 32,
									},
								}}
								alt={card.logoAlt}
								className="company-logo"
							/>
						</div>
						<Image>
							<CloudinaryImage
								url={card.imageURL}
								quality="auto"
								fetchFormat="auto"
								lazyload
								sizes={{
									default: {
										height: 64,
									},
								}}
								alt={card.imageAlt}
							/>
						</Image>
					</div>
				</div>
			</QuoteCardContent>
		);
	};

	const getQuoteCard = (card) => {
		return (
			<div className="slider-child" key={card._uid}>
				{getQuoteCardContent(card, "paragraph_M")}
			</div>
		);
	};

	const get3UpCards = (card) => {
		return (
			<div className="card-wrapper" key={card._uid}>
				{getQuoteCardContent(card, "paragraph_M")}
			</div>
		);
	};

	const getCardSection = () => {
		switch (isMobile && mobileLayout ? mobileLayout : layout) {
			case "image":
				return (
					<ImageTestimonialWrapper>
						<div className="slider-container">
							<Slider
								carouselData={cards.map(getImageCard)}
								otherSettings={sliderSettings}
								darkBg={darkBg}
							/>
						</div>
					</ImageTestimonialWrapper>
				);
			case "quote":
				return (
					<QuoteTestimonialWrapper>
						<div className="slider-container">
							<Slider
								carouselData={cards.map(getQuoteCard)}
								otherSettings={sliderSettings}
								darkBg={darkBg}
							/>
						</div>
					</QuoteTestimonialWrapper>
				);
			case "3up":
				return (
					<ThreeUpWrapper>
						<div className="three-up-container">
							{cards.slice(0, 3).map(get3UpCards)}
						</div>
					</ThreeUpWrapper>
				);
			default:
				return null;
		}
	};

	return (
		<Wrapper
			className="testimonial"
			darkBg={darkBg}
			backgroundColor={backgroundColor}
			hideNav={!showNavigationInMobile}
		>
			<Grid
				className={`content ${
					(isMobile && mobileLayout ? mobileLayout : layout) === "3up"
						? "three-up-content"
						: null
				}`}
			>
				<Col
					start={4}
					span={6}
					mdStart={1}
					mdSpan={6}
					className="centered-text"
				>
					<Text variant="headline_3" tag="h2" style={darkTextStyles}>
						{heading}
					</Text>
					<Text
						variant="paragraph_L"
						className="padding-top"
						style={darkTextStyles}
						tag="div"
						dangerouslySetInnerHTML={{ __html: description }}
					/>
				</Col>
				<Col start={1} span={12} mdStart={1} mdSpan={6}>
					{getCardSection()}
				</Col>
				{cta ? (
					<Col start={1} span={12} mdStart={1} mdSpan={6} className="btn-col">
						<Button
							{...cta}
							buttonConfiguration={cta}
							className="btn"
							type={cta.type}
							buttonText={cta.buttonText}
							onClick={() =>
								handleCTAClick(cta, {
									page_section: pageSection,
									click_text: cta?.buttonText,
									redirect_to: cta?.link?.link,
								})
							}
							darkBg={darkBg}
						/>
					</Col>
				) : null}
			</Grid>
			{!isB2U && (
				<LineGraphic
					line="squiggly"
					stroke="var(--primary-green-600)"
					x={!isMobile ? "2%" : "15%"}
					y={!isMobile ? "40%" : "0"}
					rotate={!isMobile ? "30" : "-20"}
					size={!isMobile ? "1100px" : "150vw"}
					flip={!isMobile}
					bottom={isMobile ? "50px" : undefined}
					right={isMobile ? "0" : undefined}
					top={!isMobile ? "0" : undefined}
					left={!isMobile ? "0" : undefined}
				/>
			)}
		</Wrapper>
	);
};

export default Testimonial;
